// Uitklap per persoon in Capaciteit → Per dag: dezelfde blokken als Planning → Rooster
// (Joris, 27 sep: "waarom is hier geen blokkenschema? Liefst wil je kunnen schuiven met
// uren en taken"). Hergebruikt de rij van het Rooster (PersoonRij: projectblokken met
// segmenten, draaidag oranje/optie gearceerd, afspraken grijs, grijze vulling voor klein
// werk) op het raster van de capaciteitstabel, zodat de blokken exact onder de dagkolommen
// liggen. Slepen = datums verzetten (zelfde regels als Rooster/Dagstart), rand slepen =
// korter/langer (het handvat van de Dagstart). Wegschrijven doet de pagina via het gedeelde
// verzetpad (PlannerTodo/verzetten), met ongedaan maken; de uren per dag verdeelt de
// capaciteitsberekening daarna zelf opnieuw.
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Typography } from '@mui/material';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { PersoonRij } from '../PlannerTodo/RoosterOnderdelen';
import { SleepVoorbeeld } from '../PlannerTodo/RoosterGrid';
import { botsing } from '../PlannerTodo/useRooster';
import {
  persoonBlokken, agendaBlokTaken, urenVan, dagDatum, kortTaak, DAGNAMEN, MAANDEN,
} from '../PlannerTodo/rooster';
import { verzettingUitDrop, blokMelding } from '../PlannerTodo/verzetten';
import { useTimelineDrag } from '../Dagstart/useTimelineDrag';
import { isZelfdeAfspraak } from '../../utils/draaidagenPlanning';

const STIL = '#94a3b8';

/** De dagen van de capaciteitstabel (alleen werkdagen) als kolommen in Rooster-vorm. */
export function capaciteitKolommen(days) {
  return (days || []).map((d, i) => {
    const dt = d.date;
    return {
      iso: d.iso, date: dt,
      dagNaam: DAGNAMEN[dt.getDay()], dagNr: dt.getDate(), maand: MAANDEN[dt.getMonth()],
      weekend: false,
      // Weekscheiding alleen tussen weken (de eerste kolom heeft er geen).
      maandag: dt.getDay() === 1 && i > 0,
      weekNr: d.weekNr, eersteVanMaand: dt.getDate() === 1 || i === 0,
    };
  });
}

/** Tijdens rand slepen: de taak alvast met zijn nieuwe datums tekenen. */
export const metVoorbeeld = (taken, gebaar) => (gebaar
  ? (taken || []).map((t) => (t.id === gebaar.id ? { ...t, startDate: gebaar.startDate, endDate: gebaar.endDate } : t))
  : (taken || []));

/**
 * @param persoon      { id, name }
 * @param taken        taken van deze persoon zoals de capaciteit ze rekent (incl. draaidagen)
 * @param events       Outlook-afspraken van deze persoon
 * @param afwezig      ['YYYY-MM-DD'] afwezige dagen
 * @param agendaPerDag { iso: [afspraak] } — voor de hover-tekst van de dag
 * @param load         computeDayLoad-uitkomst van de hoofdrij (uren, staat, opbouw, extra)
 * @param days         de dagkolommen van de tabel
 * @param naamKolom / dagSjabloon  het raster van de tabel (naamkolom · dagkolommen · vrij-kolom)
 * @param onVerzet     (plan: [{ taak, startDate, endDate }], melding) => Promise
 * @param onTaakOpen   taak => void (TaakPaneel)
 */
export default function CapaciteitBlokken({
  persoon, taken, events, afwezig, agendaPerDag, load, days, naamKolom, dagSjabloon, rules, onVerzet, onTaakOpen,
}) {
  const kolommen = useMemo(() => capaciteitKolommen(days), [days]);
  const [activeTask, setActiveTask] = useState(null);
  const [overCell, setOverCell] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Rand slepen: het gebaar van de Dagstart-tijdlijn (werkdagen, klemmen, Escape = afbreken).
  const { gebaar, startGebaar } = useTimelineDrag({
    onKlaar: ({ taak, startDate, endDate }) => onVerzet(
      [{ taak, startDate, endDate }],
      `${kortTaak(taak.name) || 'Taak'} loopt nu ${dagDatum(startDate)}${endDate !== startDate ? ` t/m ${dagDatum(endDate)}` : ''}`,
    ),
  });
  // Kolombreedte uit het getekende blok zelf (het handvat zit erin): klopt ook als de
  // dagkolommen bij 2/4 weken breder zijn dan het scherm en de tabel horizontaal scrolt.
  const onRand = (event, kant, taak, blok) => {
    const balk = event.currentTarget?.parentElement;
    const breedte = balk?.getBoundingClientRect().width || 0;
    const kolommenBreed = blok ? blok.c1 - blok.c0 + 1 : 1;
    // + 4 px: het blok heeft 2 px marge aan elke kant binnen zijn kolommen.
    const kolomBreedte = breedte > 0 ? (breedte + 4) / kolommenBreed : 0;
    startGebaar({ event, taak, soort: kant, kolomBreedte });
  };

  const blokken = useMemo(() => {
    const zicht = metVoorbeeld(taken, gebaar);
    // Afspraken van 3 u of langer als stil grijs blok, zoals in het Rooster.
    const draaidagen = (taken || []).filter((t) => t.bronDraaidag);
    const agenda = agendaBlokTaken(events, { afwezig, draaidagen, zelfde: isZelfdeAfspraak });
    return persoonBlokken([...zicht, ...agenda], kolommen);
  }, [taken, gebaar, events, afwezig, kolommen]);

  const dnd = {
    sensors,
    collisionDetection: botsing,
    onDragStart: (event) => {
      const d = event.active.data.current;
      if (d?.type === 'task-bar') setActiveTask(d.task);
      else if (d?.type === 'blok') setActiveTask({ ...d.tasks[0], name: `${d.label || 'Project'} — ${d.tasks.length} taken`, hours: urenVan(d.tasks) });
    },
    onDragOver: (event) => {
      const o = event.over?.data?.current;
      setOverCell(o?.type === 'capacity-cell' ? { personId: o.personId, date: o.date } : null);
    },
    onDragCancel: () => { setActiveTask(null); setOverCell(null); },
    onDragEnd: async (event) => {
      setActiveTask(null);
      setOverCell(null);
      const v = verzettingUitDrop(event.active?.data?.current, event.over?.data?.current);
      if (!v) return;
      // Zelfde regel als de Dagstart: hier verzet je in de tijd; een collega kiezen gaat via de taak.
      if (v.soort === 'collega') {
        toast('Naar een collega verplaatsen: open de taak en kies bij Toegewezen', { icon: '👤' });
        return;
      }
      if (v.soort === 'blok') {
        await onVerzet(v.taken.map(({ taak, startDate, endDate }) => ({ taak, startDate, endDate })), blokMelding(v.taken.length, v.delta));
        return;
      }
      await onVerzet([{ taak: v.taak, startDate: v.startDate, endDate: v.endDate }], `${kortTaak(v.taak.name) || 'Taak'} verzet naar ${dagDatum(v.startDate)}`);
    },
  };

  const leeg = !blokken.geplaatst.length;
  return (
    <DndContext {...dnd}>
      <div data-testid="capaciteit-blokken">
        <PersoonRij
          person={persoon}
          label={persoon.name}
          kolommen={kolommen}
          sjabloon={`${dagSjabloon} 70px`}
          naamKolom={naamKolom}
          blokken={blokken}
          load={load || { uren: {}, staat: {} }}
          rules={rules}
          overCell={overCell}
          agendaPerDag={agendaPerDag || {}}
          onTaakOpen={onTaakOpen}
          onRand={onRand}
          zonderDagSignaal
          naamInhoud={(
            <Typography sx={{ px: 2, pt: 1, fontSize: '0.75rem', color: STIL, lineHeight: 1.4 }}>
              {leeg ? 'Niets ingepland in deze periode.' : 'Sleep om te verzetten · rand = korter/langer'}
            </Typography>
          )}
        />
      </div>
      <SleepVoorbeeld activeTask={activeTask} overCell={null} />
    </DndContext>
  );
}
